import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api';
import usePolling from '../utils/usePolling';
import Card from '../components/Card';
import StatTile from '../components/StatTile';
import SearchableSelect from '../components/SearchableSelect';
import { EMPTY_TEXT } from '../components/copy';
import { CardSkeleton, Icon, PageHeader, Tabs } from '../components/ui';
import {
  TimeChart, HBarChart, Donut,
  formatNumber, formatCurrency, formatPercent,
  PALETTE, CHANNEL_COLORS,
} from '../components/charts';
import RouterInsights from '../components/dashboard/RouterInsights';
import VoiceUsage from '../components/dashboard/VoiceUsage';
import Leaderboard from '../components/dashboard/Leaderboard';
import OutboundInternet from '../components/dashboard/OutboundInternet';
import LlmRequestsSection from '../components/dashboard/LlmRequestsSection';
import ToolsSection from '../components/dashboard/ToolsSection';

// ── Environmental footprint helper ────────────────────────────────────────────
// Coefficients per 1M tokens (2025-2026 research averages):
//   Claude Opus:  1.3 L water · 1.8 kg CO₂
//   Claude Sonnet:0.5 L water · 0.65 kg CO₂
//   MiniMax / qwen: 0.2 L water · 0.4 kg CO₂
function calcEnvironmentalFootprint(tokensByModel) {
  const WATER = { opus: 1.3, sonnet: 0.5, minimax: 0.2 };
  const CO2 = { opus: 1.8, sonnet: 0.65, minimax: 0.4 };
  let waterL = 0, co2Kg = 0;
  for (const [model, tokens] of Object.entries(tokensByModel)) {
    const m = (tokens || 0) / 1_000_000;
    const k = String(model).toLowerCase();
    if (k.includes('opus')) { waterL += m * WATER.opus; co2Kg += m * CO2.opus; }
    else if (k.includes('minimax') || k.includes('qwen')) { waterL += m * WATER.minimax; co2Kg += m * CO2.minimax; }
    else { waterL += m * WATER.sonnet; co2Kg += m * CO2.sonnet; }
  }
  return { waterLiters: waterL, bottles500ml: waterL * 2, co2Kg };
}

const PRESETS = [
  { label: '1 Day', days: 1 },
  { label: '1 Week', days: 7 },
  { label: '1 Month', days: 30 },
  { label: 'Custom', days: null },
];

// Infrastructure (nodes, database, services, alarms) is on the Platform page, which the
// header links to; this page is about what users do and what it costs.
const TABS = [
  { value: 'overview', label: 'Overview', icon: 'dashboard' },
  { value: 'rankings', label: 'Rankings', icon: 'analytics' },
];

// Series colours, in one place. Every chart draws from the theme palette so a line
// keeps its meaning (and its contrast) in both themes.
// Cache writes are split by how long the entry is kept, under the names the Models page
// prices them by: long cache (1 hour) and short cache (5 minutes).
const TOKEN_SERIES = [
  { key: 'input_tokens', label: 'Input', color: PALETTE[0] },
  { key: 'cache_read', label: 'Cache Read', color: PALETTE[1] },
  { key: 'cache_write_long', label: 'Long Cache Write', color: PALETTE[2] },
  { key: 'cache_write_short', label: 'Short Cache Write', color: PALETTE[6] },
  { key: 'output_tokens', label: 'Output', color: PALETTE[3] },
];

// Quota buckets: grey for "No cap" (nothing to score), then green to red by how much of
// the cap is used. Solid tokens, not gradients: a `var()` cannot take a hex alpha suffix,
// which is how the previous version of these bars ended up with no fill at all.
const QUOTA_COLORS = [
  'var(--ink-4)', 'var(--ok-solid)', 'var(--series-2)',
  'var(--warn-solid)', 'var(--series-3)', 'var(--danger-solid)',
];

function Dashboard() {
  const [filterOpts, setFilterOpts] = useState({ users: [], models: [], channels: [], tiers: [], sources: [] });
  const [filters, setFilters] = useState({
    preset: '1 Day', user_id: '', model: '', channel: '', tier_id: '', source: '',
    date_from: '', date_to: '',
  });
  // Tab system: Overview and Rankings (the leaderboard). Tab state is
  // local because the page state is short-lived and there's no deep-linking requirement;
  // if tabs grow further this should move to a URL segment.
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tokensOT, setTokensOT] = useState([]);
  const [costOT, setCostOT] = useState({ models: [], data: [] });
  const [messagesOT, setMessagesOT] = useState([]);
  const [costByTier, setCostByTier] = useState([]);
  const [tierDist, setTierDist] = useState([]);
  const [activeUsersOT, setActiveUsersOT] = useState([]);
  const [quota, setQuota] = useState({ histogram: [], near_cap_users: [] });
  // ── Percentile charts ─────────────────────────────────────────────────────
  // Four charts, p5…p99 per bucket, fetched on their own path (see fetchLatency): LLM
  // latency and tokens per request under LLM requests, tool and turn latency in Tools.
  // `llmLatMetric`, `tokMetric` and `toolLatTool` are PER-GRAPH controls rather than
  // entries in the global `filters` object: they apply to one card each, and putting them
  // in `filters` would send a tool_id to twelve endpoints that have no such concept.
  const [llmLatency, setLlmLatency] = useState({ metric: 'total', data: [] });
  const [tokensPerRequest, setTokensPerRequest] = useState({ metric: 'input', data: [] });
  const [toolLatency, setToolLatency] = useState({
    tools: [], tool_id: '', model_ignored: false, data: [],
  });
  const [turnLatency, setTurnLatency] = useState({ data: [] });
  const [llmLatMetric, setLlmLatMetric] = useState('total');
  const [tokMetric, setTokMetric] = useState('input');
  const [toolLatTool, setToolLatTool] = useState('');
  // The rest of the LLM requests section (Mark, 2026-10-01), on the same path and behind
  // the same section notice: cache hit rate per model, truncated responses, requests per
  // turn. No per-graph control, so they never change `fetchLatency`'s dependencies.
  const [cacheHitRate, setCacheHitRate] = useState({
    models: [], no_cache_models: [], window: [], data: [],
  });
  const [truncated, setTruncated] = useState({ data: [], models: [], total: null });
  const [reqsPerTurn, setReqsPerTurn] = useState({ data: [] });
  // Own error surfaces, deliberately not the page-wide `fetchError` below. These endpoints'
  // tables start empty, so a failure here says nothing about whether the other twelve
  // widgets are current. Claiming the whole page is stale because one chart 404'd on a
  // pre-deploy backend would be the same misreport in the opposite direction. One per
  // section, so a failure is reported next to the chart it emptied.
  const [latencyError, setLatencyError] = useState(null);
  const [toolLatencyError, setToolLatencyError] = useState(null);
  // The Outbound Internet section (proxy-web, 2026-10-03): its own path, controls and
  // notice, for the same reasons as fetchLatency (per-card controls that must not refetch
  // the page, and tables that start empty on every environment).
  const [outbound, setOutbound] = useState({ totals: {}, data: [], sites: [], ignored: [] });
  const [outboundTiming, setOutboundTiming] = useState({ metric: 'connect', data: [] });
  const [outboundRecent, setOutboundRecent] = useState({ rows: [] });
  const [outboundMetric, setOutboundMetric] = useState('connect');
  const [outboundResult, setOutboundResult] = useState('');
  const [outboundError, setOutboundError] = useState(null);
  // Model routers (the Overview's Routers section), on the main fetch path.
  const [routerData, setRouterData] = useState({ configured: 0, routers: [] });
  // The speech models (the Overview's Voice section), on the main fetch path too.
  const [voiceData, setVoiceData] = useState({ totals: {}, by_model: [], models: [], data: [], seconds: [] });
  // The Rankings tab: one leaderboard on its own fetch path, which runs only while the tab
  // is open. It used to be four top-50 scans on the main path, every 30 s, on either tab.
  const [lbSort, setLbSort] = useState('cost');
  const [board, setBoard] = useState(null);
  const [boardError, setBoardError] = useState(null);
  const [loading, setLoading] = useState(true);
  // This page had NO error surface: 1295 lines, zero toast.error, zero setError. A failed
  // fetch logged to the console and left whatever was already on screen, so a dashboard
  // showing hour-old numbers was indistinguishable from one showing current ones. On a page
  // whose entire job is telling you the state of the platform, that is the worst failure
  // mode available.
  //
  // `loadedAt` is what makes the difference visible. The two cases need different
  // treatment: nothing ever loaded (show the error instead of the page) versus loaded
  // once and now failing (show the page, but say how old it is).
  const [fetchError, setFetchError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  // ── Derived ────────────────────────────────────────────────────────────────
  const getDateParams = useCallback(() => {
    const p = {};
    const preset = PRESETS.find(pr => pr.label === filters.preset);
    if (preset && preset.days) {
      const from = new Date();
      from.setDate(from.getDate() - preset.days);
      p.date_from = from.toISOString().slice(0, 10);
    } else if (filters.preset === 'Custom') {
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
    }
    return p;
  }, [filters]);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    const dates = getDateParams();
    if (filters.user_id) p.set('user_id', filters.user_id);
    if (filters.model) p.set('model', filters.model);
    if (filters.channel) p.set('channel', filters.channel);
    if (filters.tier_id) p.set('tier_id', filters.tier_id);
    if (filters.source) p.set('source', filters.source);
    if (dates.date_from) p.set('date_from', dates.date_from);
    if (dates.date_to) p.set('date_to', dates.date_to);
    return p.toString() ? `?${p.toString()}` : '';
  }, [filters, getDateParams]);

  // A8: server-side typeahead so the full 20k-user list is never shipped. Memoised
  // because SearchableSelect re-runs its search when this identity changes, and an inline
  // arrow is a new identity on every render: the list was refetched on each 30 s refresh.
  const searchUsers = useCallback(async (q) => {
    const res = await apiFetch(
      `/api/dashboard/filters?user_limit=50${q ? `&q=${encodeURIComponent(q)}` : ''}`
    );
    const data = await res.json();
    return (data.users || []).map(u => ({ value: u.id, label: u.username }));
  }, []);

  const filterOptions = useMemo(() => ({
    users: filterOpts.users.map(u => ({ value: u.id, label: u.username })),
    tiers: filterOpts.tiers.map(t => ({ value: t.id, label: t.name })),
    models: filterOpts.models.map(m => ({ value: m, label: m })),
    channels: filterOpts.channels.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })),
    sources: filterOpts.sources.map(s => ({
      value: s,
      label: s === 'llm' ? 'User'
        : s === 'tools' ? 'External Tool'
        : s.charAt(0).toUpperCase() + s.slice(1),
    })),
  }), [filterOpts]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    // A8: bound the users in the initial payload (user_limit) — the users dropdown
    // is a server-side typeahead (onSearch) so we don't need the full 20k list here.
    apiFetch('/api/dashboard/filters?user_limit=50').then(r => r.json()).then(setFilterOpts).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const q = buildQuery();
      const [
        sRes, tRes, cRes, mRes,
        ctRes, tdRes, auRes, quRes,
        rtRes, vcRes,
      ] = await Promise.all([
        apiFetch(`/api/dashboard/stats${q}`),
        apiFetch(`/api/dashboard/tokens-over-time${q}`),
        apiFetch(`/api/dashboard/cost-over-time${q}`),
        apiFetch(`/api/dashboard/messages-over-time${q}`),
        // Dropped `/api/usage` (2026-05-25): the response was being
        // stored in dead state never read by any JSX. The endpoint
        // does a full O(users × usage_logs) scan with no LIMIT or
        // date filter, occasionally timing out → 500 → the dashboard
        // showed a multi-second loading state then a failed widget.
        // Endpoint itself stays on the backend (`/api/usage/{user_id}`
        // is still used elsewhere) and should be optimized separately.
        apiFetch(`/api/dashboard/cost-by-tier${q}`),
        // Tier-distribution now respects the active dashboard filters so
        // the donut reflects whatever cohort the rest of the page is showing
        // (previously it always rendered the global breakdown).
        apiFetch(`/api/dashboard/tier-distribution${q}`),
        apiFetch('/api/dashboard/active-users-over-time?days=30'),
        apiFetch('/api/dashboard/quota-utilization'),
        // The Routers section. Rankings are not here any more: they have their own path,
        // fetched only while that tab is open (fetchBoard below).
        apiFetch(`/api/dashboard/routers${q}`),
        // The Voice section: what the speech models cost, apart from the LLM cards.
        apiFetch(`/api/dashboard/voice${q}`),
      ]);
      // Defensive: an endpoint that returns an error body (e.g. 404 on a
      // pre-deploy backend) would otherwise leak a `{detail: "..."}` object
      // into widget state and crash the .filter / .map calls downstream.
      const arr = async (r) => { const j = await r.json().catch(() => null); return Array.isArray(j) ? j : []; };
      const obj = async (r) => { const j = await r.json().catch(() => null); return j && typeof j === 'object' && !Array.isArray(j) ? j : {}; };
      setStats(await obj(sRes));
      setTokensOT(await arr(tRes));
      setCostOT(await obj(cRes));
      setMessagesOT(await arr(mRes));
      setCostByTier(await arr(ctRes));
      setTierDist(await arr(tdRes));
      setActiveUsersOT(await arr(auRes));
      setQuota(await obj(quRes));
      const rt = await obj(rtRes);
      setRouterData(Array.isArray(rt.routers) ? rt : { configured: 0, routers: [] });
      const vc = await obj(vcRes);
      setVoiceData(Array.isArray(vc.by_model) ? vc
        : { totals: {}, by_model: [], models: [], data: [], seconds: [] });
      setLoading(false);
      setFetchError(null);
      setLoadedAt(new Date());
    } catch (e) {
      console.error('Dashboard fetch error:', e);
      // Deliberately NOT a toast. A toast closes itself after three seconds and the stale
      // numbers stay on screen afterwards with nothing to mark them, which is the exact
      // condition this is here to make visible. The banner persists until a fetch succeeds.
      setFetchError(e?.message || 'the request failed');
      setLoading(false);
    }
  }, [buildQuery]);

  // Every 30s while the tab is visible; a new filter reloads at once (utils/usePolling).
  usePolling(fetchData, 30000, [fetchData]);

  // ── Latency fetch — a SECOND path, and the reason is the per-graph controls ──
  //
  // `fetchData` is memoised on `buildQuery` alone, so reading `llmLatMetric` or
  // `toolLatTool` inside it would capture whichever value was current when the callback
  // was built and never see a change. Adding them to its dependency list fixes that but
  // makes every dropdown click refetch all twelve endpoints, four of which are top-50
  // ranking scans: clicking through ten tools would be 120 queries to move three lines
  // on one card.
  //
  // So: one extra path, not seven. Every endpoint of the LLM requests section and the two
  // latency cards in Tools share it (the three with per-graph controls and the four
  // without), because splitting per-card would mean seven intervals and seven error
  // states. Same 30s cadence as the main path.
  const fetchLatency = useCallback(async () => {
    try {
      const q = buildQuery();
      const extra = (k, v) => (q ? `${q}&${k}=${v}` : `?${k}=${v}`);
      const [llmRes, tokRes, toolRes, turnRes, cacheRes, cutRes, rptRes] = await Promise.all([
        apiFetch(`/api/dashboard/llm-latency${extra('metric', llmLatMetric)}`),
        apiFetch(`/api/dashboard/tokens-per-request${extra('metric', tokMetric)}`),
        apiFetch(`/api/dashboard/tool-latency${
          toolLatTool ? extra('tool_id', encodeURIComponent(toolLatTool)) : q}`),
        apiFetch(`/api/dashboard/turn-latency${q}`),
        apiFetch(`/api/dashboard/cache-hit-rate${q}`),
        apiFetch(`/api/dashboard/truncated-responses${q}`),
        apiFetch(`/api/dashboard/requests-per-turn${q}`),
      ]);
      // Same defensive unwrap as the main path: a `{detail: "..."}` error body would
      // otherwise reach `.map` on `data` and take the page down.
      //
      // ⚠ AND `apiFetch` RESOLVES ON 404/500 — it only throws on an unrecoverable 401 — so
      // the catch below never sees a failed endpoint. That matters more here than anywhere
      // else on this page: these charts are legitimately empty on the day they ship, so a
      // broken route would render as "No latency recorded in this window" and be
      // indistinguishable from the real initial state. Failures are collected and named,
      // per section, so each notice sits next to the charts it concerns.
      const failed = { latency: [], tools: [] };
      const pct = async (r, label, section, fallback) => {
        if (!r.ok) {
          failed[section].push(`${label} (HTTP ${r.status})`);
          return fallback;
        }
        const j = await r.json().catch(() => null);
        if (!j || typeof j !== 'object' || Array.isArray(j) || !Array.isArray(j.data)) {
          failed[section].push(`${label} (unexpected response)`);
          return fallback;
        }
        return j;
      };
      setLlmLatency(await pct(llmRes, 'LLM latency', 'latency',
        { metric: llmLatMetric, data: [] }));
      setTokensPerRequest(await pct(tokRes, 'tokens per request', 'latency',
        { metric: tokMetric, data: [] }));
      setToolLatency(await pct(toolRes, 'tool latency', 'tools', {
        tools: [], tool_id: toolLatTool, model_ignored: false, data: [],
      }));
      setTurnLatency(await pct(turnRes, 'turn latency', 'tools', { data: [] }));
      setCacheHitRate(await pct(cacheRes, 'cache hit rate', 'latency',
        { models: [], no_cache_models: [], window: [], data: [] }));
      setTruncated(await pct(cutRes, 'truncated responses', 'latency',
        { data: [], models: [], total: null }));
      setReqsPerTurn(await pct(rptRes, 'requests per turn', 'latency', { data: [] }));
      setLatencyError(failed.latency.length ? failed.latency.join(', ') : null);
      setToolLatencyError(failed.tools.length ? failed.tools.join(', ') : null);
    } catch (e) {
      console.error('Dashboard latency fetch error:', e);
      setLatencyError(e?.message || 'the request failed');
      setToolLatencyError(e?.message || 'the request failed');
    }
  }, [buildQuery, llmLatMetric, toolLatTool, tokMetric]);

  usePolling(fetchLatency, 30000, [fetchLatency]);

  // ── Outbound Internet fetch: a fourth path, live only on the Overview tab ────
  // Same shape and the same failure naming as fetchLatency. The timing metric and the
  // result filter are per-card controls, so a click refetches these three and no more.
  const fetchOutbound = useCallback(async () => {
    try {
      const q = buildQuery();
      const extra = (k, v) => (q ? `${q}&${k}=${v}` : `?${k}=${v}`);
      const [sumRes, timeRes, recRes] = await Promise.all([
        apiFetch(`/api/dashboard/outbound${q}`),
        apiFetch(`/api/dashboard/outbound-timing${extra('metric', outboundMetric)}`),
        apiFetch(`/api/dashboard/outbound-requests${
          outboundResult ? extra('outcome', outboundResult) : q}`),
      ]);
      const failed = [];
      const read = async (r, label, valid, fallback) => {
        if (!r.ok) {
          failed.push(`${label} (HTTP ${r.status})`);
          return fallback;
        }
        const j = await r.json().catch(() => null);
        if (!j || typeof j !== 'object' || Array.isArray(j) || !valid(j)) {
          failed.push(`${label} (unexpected response)`);
          return fallback;
        }
        return j;
      };
      setOutbound(await read(sumRes, 'internet requests',
        j => Array.isArray(j.data) && Array.isArray(j.sites),
        { totals: {}, data: [], sites: [], ignored: [] }));
      setOutboundTiming(await read(timeRes, 'time per request', j => Array.isArray(j.data),
        { metric: outboundMetric, data: [] }));
      setOutboundRecent(await read(recRes, 'recent requests', j => Array.isArray(j.rows),
        { rows: [] }));
      setOutboundError(failed.length ? failed.join(', ') : null);
    } catch (e) {
      console.error('Dashboard outbound fetch error:', e);
      setOutboundError(e?.message || 'the request failed');
    }
  }, [buildQuery, outboundMetric, outboundResult]);

  usePolling(fetchOutbound, activeTab === 'overview' ? 30000 : 0, [fetchOutbound]);

  // ── Leaderboard fetch: a THIRD path, live only on the Rankings tab ──────────
  // Same shape as fetchLatency: its own control (the sort) would otherwise refetch the
  // whole page, and an Overview reader has no use for a scan of every user's activity.
  // `apiFetch` resolves on an HTTP error, so a failed response is named, not ignored.
  const fetchBoard = useCallback(async () => {
    try {
      const q = buildQuery();
      const res = await apiFetch(`/api/dashboard/leaderboard${q ? `${q}&` : '?'}sort=${lbSort}&limit=50`);
      if (!res.ok) {
        setBoardError(`HTTP ${res.status}`);
        return;
      }
      const j = await res.json().catch(() => null);
      if (!j || !Array.isArray(j.rows) || !j.totals) {
        setBoardError('unexpected response');
        return;
      }
      setBoard(j);
      setBoardError(null);
    } catch (e) {
      console.error('Dashboard leaderboard fetch error:', e);
      setBoardError(e?.message || 'the request failed');
    }
  }, [buildQuery, lbSort]);

  usePolling(fetchBoard, activeTab === 'rankings' ? 30000 : 0, [fetchBoard]);

  // ── Pivots / derivations ───────────────────────────────────────────────────
  const totalTokens = stats && stats.tokens
    ? (stats.tokens.total_input || 0) + (stats.tokens.total_output || 0)
      + (stats.tokens.total_cache_read || 0) + (stats.tokens.total_cache_write || 0)
    : 0;

  // Provider mix (A8) — donut data from stats.tokens.by_provider.
  const providerMix = useMemo(() => {
    const arr = stats?.tokens?.by_provider || [];
    return arr
      .filter(r => (r.total_tokens || 0) > 0)
      .map((r, i) => ({ name: r.provider || 'unknown', value: r.total_tokens, color: PALETTE[i % PALETTE.length] }));
  }, [stats]);

  // Tier distribution (A3) — donut.
  const tierDistData = useMemo(() => {
    return (tierDist || [])
      .filter(r => (r.user_count || 0) > 0)
      .map((r, i) => ({ name: r.tier_name, value: r.user_count, color: PALETTE[i % PALETTE.length] }));
  }, [tierDist]);

  // Channel mix (A6) — donut from stats.messages.by_channel.
  const channelMix = useMemo(() => {
    const obj = stats?.messages?.by_channel || {};
    return Object.entries(obj)
      .map(([ch, v]) => ({
        name: ch,
        value: (v.inbound || 0) + (v.outbound || 0),
        color: CHANNEL_COLORS[ch] || PALETTE[0],
      }))
      .filter(r => r.value > 0);
  }, [stats]);

  // Active-users sparkline + DAU/WAU/MAU (A4).
  const auSparkline = useMemo(() => (activeUsersOT || []).map(r => ({
    label: r.date, value: r.active_users,
  })), [activeUsersOT]);
  const auSummary = useMemo(() => {
    const arr = activeUsersOT || [];
    if (arr.length === 0) return { dau: 0, w7: 0, m30: 0 };
    const last = (n) => arr.slice(-n);
    const avg = (xs) => xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : 0;
    return {
      dau: arr[arr.length - 1].active_users || 0,
      w7: avg(last(7).map(r => r.active_users || 0)),
      m30: avg(last(30).map(r => r.active_users || 0)),
    };
  }, [activeUsersOT]);

  // Quota histogram (A7).
  const quotaHist = useMemo(() => (quota?.histogram || []).map(r => ({ bucket: r.bucket, count: r.count })), [quota]);
  const quotaAtRisk = useMemo(() => (quota?.near_cap_users || []).length, [quota]);

  // Cost-by-tier (A2) — horizontal bars, cost vs cap envelope.
  const costByTierBars = useMemo(() => (costByTier || [])
    .filter(r => (r.period_cost_eur || 0) > 0 || (r.envelope_eur || 0) > 0)
    .map(r => ({
      name: r.tier_name,
      count: r.period_cost_eur,
      envelope: r.envelope_eur,
      synthetic: r.synthetic,
      pct_of_envelope: r.envelope_eur ? (r.period_cost_eur / r.envelope_eur) : null,
    })), [costByTier]);

  // Environmental footprint — distribute total tokens by per-model cost share.
  const env = useMemo(() => {
    if (totalTokens === 0) return null;
    const costModels = costOT.models || [];
    const costData = costOT.data || [];
    const modelTotals = {};
    for (const m of costModels) {
      modelTotals[m] = costData.reduce((s, d) => s + (d[m] || 0), 0);
    }
    const totalCost = Object.values(modelTotals).reduce((a, b) => a + b, 0);
    const tokensByModel = {};
    if (totalCost > 0) {
      for (const [m, c] of Object.entries(modelTotals)) {
        tokensByModel[m] = totalTokens * (c / totalCost);
      }
    } else {
      tokensByModel.sonnet = totalTokens;
    }
    return calcEnvironmentalFootprint(tokensByModel);
  }, [totalTokens, costOT]);

  // ── UI ─────────────────────────────────────────────────────────────────────
  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const refreshAll = () => {
    fetchData();
    fetchLatency();
    if (activeTab === 'rankings') fetchBoard();
  };
  const anyFilter = Boolean(filters.user_id || filters.model || filters.channel
    || filters.tier_id || filters.source);

  // The page heading renders in every state, so a failed or loading page still says
  // what it is.
  const header = (
    <PageHeader
      description="Spend, traffic and agent activity across the tenant. Everything below answers to the filters, and the window they cover is shown on each chart."
      meta={loadedAt && (
        <div className="page-meta">
          <span>Updated {loadedAt.toLocaleTimeString()}</span>
          <span>Refreshes every 30 s</span>
        </div>
      )}
      actions={(
        <div className="row row-wrap gap-2">
          <Link to="/platform" className="btn btn-secondary btn-sm">
            <Icon name="infrastructure" />
            Platform health
          </Link>
          <button type="button" className="btn btn-secondary btn-sm" onClick={refreshAll}>
            <Icon name="refresh" />
            Refresh
          </button>
        </div>
      )}
    >
      <h1 className="page-title">Dashboard</h1>
    </PageHeader>
  );

  // Nothing ever loaded. Showing "Loading…" forever was the old behaviour and it reads as a
  // slow page rather than a failed one, so the retry is never attempted.
  if (fetchError && !stats) {
    return (
      <div>
        {header}
        <div className="card load-failed" role="alert">
          <Icon name="alertCircle" size={20} />
          <div className="grow">
            <h2 className="m-0 text-md">Could not load the dashboard</h2>
            <p className="text-muted mt-1 mb-0">{fetchError}</p>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={fetchData}>Try again</button>
        </div>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div>
        {header}
        <div className="dashboard-grid" role="status" aria-busy="true">
          <span className="sr-only">Loading…</span>
          {[0, 1, 2, 3].map(i => <div key={i} className="dashboard-tile-3"><CardSkeleton lines={2} /></div>)}
          {[0, 1].map(i => <div key={`c${i}`} className="dashboard-tile-6"><CardSkeleton lines={6} /></div>)}
        </div>
      </div>
    );
  }

  // Cost-vs-Cap totals (sum of period vs sum of envelope across tiers).
  const totalCostUsd = stats.cost?.total_usd || 0;
  const totalEnvelope = (costByTier || []).reduce((s, t) => s + (t.envelope_eur || 0), 0);
  const costVsCapPct = totalEnvelope > 0 ? (totalCostUsd / totalEnvelope) * 100 : null;
  const costModels = costOT.models || [];
  const costData = costOT.data || [];

  return (
    <div>
      {header}

      {/* Loaded once, now failing. The numbers below are real but old, and saying WHEN is
          the whole point: "something went wrong" would leave the reader deciding whether to
          trust them. Sits above the filters so it is read before any figure is. */}
      {fetchError && (
        <div role="status" className="notice notice-warn">
          <div className="notice-body">
            <strong>Showing older data.</strong> The last refresh failed ({fetchError}).
            {loadedAt && ` These figures are from ${loadedAt.toLocaleTimeString()}.`}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={fetchData}>Retry</button>
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────────────
          One bar for the whole page: the window on the first row, the cohort on the
          second. Every tab answers to both. The bar lets its popups escape (overflow is
          visible) so a SearchableSelect near the bottom of the card is not clipped. */}
      <div className="card filter-bar">
        <div className="filter-bar-row">
          <span className="filter-bar-label">Window</span>
          <Tabs size="sm" label="Time window" value={filters.preset}
                onChange={v => setFilter('preset', v)}
                tabs={PRESETS.map(p => ({ value: p.label, label: p.label }))} />
          {filters.preset === 'Custom' && (
            <div className="row gap-2">
              <input type="date" className="form-input date-input" aria-label="From date"
                     value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
              <span className="text-muted text-sm">to</span>
              <input type="date" className="form-input date-input" aria-label="To date"
                     value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
            </div>
          )}
        </div>
        <div className="filter-bar-row">
          <span className="filter-bar-label">Cohort</span>
          <SearchableSelect
            value={filters.user_id}
            onChange={(v) => setFilter('user_id', v)}
            placeholder="All Users"
            searchPlaceholder="Search users…"
            aria-label="Filter by user"
            options={filterOptions.users}
            onSearch={searchUsers}
            minWidth={150}
          />
          <SearchableSelect
            value={filters.tier_id}
            onChange={(v) => setFilter('tier_id', v)}
            placeholder="All Tiers"
            searchPlaceholder="Search tiers…"
            aria-label="Filter by tier"
            options={filterOptions.tiers}
            minWidth={140}
          />
          <SearchableSelect
            value={filters.model}
            onChange={(v) => setFilter('model', v)}
            placeholder="All Models"
            searchPlaceholder="Search models…"
            aria-label="Filter by model"
            options={filterOptions.models}
            minWidth={150}
          />
          <SearchableSelect
            value={filters.channel}
            onChange={(v) => setFilter('channel', v)}
            placeholder="All Channels"
            searchPlaceholder="Search channels…"
            aria-label="Filter by channel"
            options={filterOptions.channels}
            minWidth={140}
          />
          <SearchableSelect
            value={filters.source}
            onChange={(v) => setFilter('source', v)}
            placeholder="All Sources"
            searchPlaceholder="Search sources…"
            aria-label="Filter by source"
            options={filterOptions.sources}
            minWidth={140}
          />
          {anyFilter && (
            <button
              type="button"
              className="btn btn-ghost btn-sm text-danger"
              onClick={() => setFilters(prev => ({
                ...prev, user_id: '', model: '', channel: '', tier_id: '', source: '',
              }))}>
              <Icon name="x" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {(filters.user_id || filters.tier_id || filters.source) && (
        <div className="dashboard-filter-hint">
          Filtered view: data older than the retention threshold (configurable in
          Data Governance) is not shown when filtering by user, tier, or source.
        </div>
      )}

      <div className="dashboard-tabs">
        <Tabs variant="line" label="Dashboard views" idPrefix="dash"
              value={activeTab} onChange={setActiveTab} tabs={TABS} />
      </div>

      <div role="tabpanel" id={`dash-panel-${activeTab}`} aria-labelledby={`dash-tab-${activeTab}`}>

      {/* ══ Overview tab content (everything until the close marker below) ══ */}
      {activeTab === 'overview' && (<>

      {/* ── KPI tiles ─────────────────────────────────────────────────────── */}
      <div className="dashboard-grid">
        <StatTile
          span={3}
          icon="users"
          label="Active Users"
          value={formatNumber(stats.users?.active || 0)}
          sub={`${auSummary.w7} avg/day · last 7d`}
          sparkline={auSparkline}
          help="Users with deleted_at IS NULL, i.e. accounts that haven't been wiped yet. Sparkline shows DAU over the last 30 days; 'avg/day · last 7d' is the mean daily active count over the past week."
        />
        <StatTile
          span={3}
          icon="analytics"
          label="Total Cost"
          value={formatCurrency(totalCostUsd)}
          sub={`${formatNumber(stats.tokens?.total_requests || 0)} LLM requests`}
          tone={costVsCapPct != null && costVsCapPct > 80 ? 'warning' : undefined}
          help="Total cost in EUR for the selected date range and filter set: LLM requests and speech calls, as the users' budgets count them. A router call counts at the cost the router stated; a speech call at its seconds of audio times its price per second on the Voice page; any other call at its tokens times its model's prices on the Models page."
        />
        <StatTile
          span={3}
          icon="gauge"
          label="Cost vs Cap"
          value={costVsCapPct == null ? '—' : formatPercent(costVsCapPct, 0)}
          sub={costVsCapPct == null ? 'no caps configured'
            : `${formatCurrency(totalEnvelope)} envelope`}
          tone={costVsCapPct != null && costVsCapPct > 90 ? 'negative'
            : costVsCapPct != null && costVsCapPct > 70 ? 'warning' : 'positive'}
          help={
            'Total period spend ÷ total cap envelope. The envelope is the theoretical worst-case monthly cost: '
            + 'sum of (user_count × tier.monthly_cost_limit) across every tier with a positive cap. '
            + 'Tile turns yellow at >70% and red at >90%. Shows "—" when no tier has a monthly_cost_limit set.'
          }
        />
        {/* Was labelled "Live Containers" and described as "ECS user-container
            tasks in RUNNING state". Both are wrong now: agents are Kubernetes
            Deployments, the per-user ECS service model is gone, and the copy
            standard says `agent` in user-facing text either way. */}
        <StatTile
          span={3}
          icon="bot"
          label="Live agents"
          value={formatNumber(stats.containers?.running || 0)}
          sub={quotaAtRisk > 0
            ? `${quotaAtRisk} user${quotaAtRisk === 1 ? '' : 's'} >80% quota`
            : 'all users below 80% cap'}
          tone={quotaAtRisk > 0 ? 'warning' : 'positive'}
          help="Agents running right now. The sub-line counts users whose period_cost_usd ÷ tier.monthly_cost_limit is over 80%, the next likely cap overruns."
        />
      </div>

      {/* ── Spend: quota utilisation beside cost by tier ────────────────────── */}
      <div className="section-head">
        <h2>Spend</h2>
        <p>against the configured caps</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Quota utilization"
              subtitle="users by % of monthly cap"
              span={6}
              tone={quotaAtRisk > 0 ? 'warning' : undefined}
              empty={(quotaHist || []).every(r => !r.count)
                ? 'No active users in tiers with a monthly cost cap'
                : null}>
          <HBarChart
            data={quotaHist}
            labelKey="bucket"
            valueKey="count"
            nameWidth={70}
            formatVal={(v) => `${formatNumber(v)} user${v === 1 ? '' : 's'}`}
            colorFn={(_, i) => QUOTA_COLORS[i % QUOTA_COLORS.length]}
          />
        </Card>
        <Card title="Cost by tier" subtitle="period spend vs cap envelope"
              help="The envelope is user_count × monthly_cost_limit for the tier: what the tier would cost if every user spent their whole cap. Bars turn red when period spend passes 80% of it."
              span={6}
              empty={costByTierBars.length === 0 ? 'No tier spend yet' : null}>
          <HBarChart
            data={costByTierBars}
            valueKey="count"
            nameWidth={70}
            formatVal={formatCurrency}
            colorFn={(row) => row.synthetic
              ? 'var(--ink-4)'
              : row.pct_of_envelope != null && row.pct_of_envelope > 0.8
                ? 'var(--danger-solid)'
                : 'var(--ok-solid)'}
          />
        </Card>
      </div>

      {/* ── Distribution: Provider · Tier · Channel ─────────────────────────── */}
      <div className="section-head">
        <h2>Distribution</h2>
        <p>where the load sits</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Provider mix" subtitle="tokens by provider" span={4}
              empty={providerMix.length === 0 ? 'No usage yet' : null}>
          <Donut data={providerMix} centerLabel={formatNumber(totalTokens)} centerSub="tokens" height={220} />
        </Card>
        <Card title="Tier distribution" subtitle="active users by tier" span={4}
              empty={tierDistData.length === 0 ? 'No active users' : null}>
          <Donut data={tierDistData}
                 centerLabel={formatNumber(tierDistData.reduce((s, r) => s + r.value, 0))}
                 centerSub="users" height={220} />
        </Card>
        <Card title="Channel mix" subtitle="messages by channel" span={4}
              empty={channelMix.length === 0 ? 'No messages yet' : null}>
          <Donut data={channelMix}
                 centerLabel={formatNumber(channelMix.reduce((s, r) => s + r.value, 0))}
                 centerSub="messages" height={220} />
        </Card>
      </div>

      {/* ── Activity ────────────────────────────────────────────────────────── */}
      <div className="section-head">
        <h2>Activity</h2>
      </div>
      <div className="dashboard-grid">
        <Card title="Active users" subtitle="DAU / 7-day avg / 30-day avg" span={12}>
          <div className="au-split">
            <dl className="au-figures">
              <div className="au-figure au-figure-main">
                <dt>Daily</dt>
                <dd className="num">{formatNumber(auSummary.dau)}</dd>
              </div>
              <div className="au-figure">
                <dt>7-day avg</dt>
                <dd className="num">{formatNumber(auSummary.w7)}</dd>
              </div>
              <div className="au-figure">
                <dt>30-day avg</dt>
                <dd className="num">{formatNumber(auSummary.m30)}</dd>
              </div>
            </dl>
            <div className="min-w-0">
              <TimeChart
                data={(activeUsersOT || []).map(r => ({ date: r.date, users: r.active_users }))}
                labelKey="date"
                lines={[{ key: 'users', label: 'Active' }]}
                colors={[PALETTE[0]]}
                height={200}
              />
            </div>
          </div>
        </Card>
      </div>

      {/* ── Over time ───────────────────────────────────────────────────────── */}
      <div className="section-head">
        <h2>Over time</h2>
      </div>
      <div className="dashboard-grid">
        <Card title="Token usage over time"
              subtitle={`${formatNumber(totalTokens)} total · ${formatNumber(stats.tokens?.total_requests || 0)} requests`}
              help="Cache writes are split by how long the cache keeps them: long cache for 1 hour, short cache for 5 minutes, each with its own price on the Models page. Writes recorded before 9 October 2026 all show as long cache."
              span={12}>
          <TimeChart data={tokensOT} labelKey="date"
            lines={TOKEN_SERIES.map(({ key, label }) => ({ key, label }))}
            colors={TOKEN_SERIES.map(s => s.color)}
            height={260}
            legend
            tooltipExtra={(d) => (
              <div className="chart-tooltip-foot">
                <span className="chart-tooltip-label">Users</span>
                <span className="chart-tooltip-value">{d.unique_users || 0}</span>
                <span className="chart-tooltip-label ml-2">Requests</span>
                <span className="chart-tooltip-value">{d.requests || 0}</span>
              </div>
            )} />
        </Card>
        <Card title="Cost over time (EUR)" subtitle="per LLM model" span={6}
              help="The LLM requests' cost per model. The speech models' cost is in the Voice section, and Total Cost counts both."
              empty={costData.length === 0 ? 'No cost data yet' : null}>
          <TimeChart data={costData} labelKey="date"
                     lines={costModels.map(m => ({ key: m, label: m }))}
                     colors={costModels.map((_, i) => PALETTE[(i + 4) % PALETTE.length])}
                     height={240}
                     legend
                     formatY={v => '€' + (v < 10 ? v.toFixed(1) : formatNumber(Math.round(v)))} />
        </Card>
        <Card title="Messages over time" subtitle="inbound vs outbound" span={6}>
          <TimeChart data={messagesOT} labelKey="date"
            lines={[
              { key: 'inbound', label: 'Inbound' },
              { key: 'outbound', label: 'Outbound' },
            ]}
            colors={[PALETTE[1], PALETTE[4]]}
            height={240}
            legend />
        </Card>
      </div>

      {/* ── LLM requests: time, tokens, cache use and cut-offs per request ─────
          Shared with the load tests' report: components/dashboard/LlmRequestsSection,
          where its cards are explained. Its data comes on its own path, fetchLatency,
          behind its own notice. */}
      <LlmRequestsSection
        latency={llmLatency} latencyMetric={llmLatMetric} onLatencyMetric={setLlmLatMetric}
        tokens={tokensPerRequest} tokensMetric={tokMetric} onTokensMetric={setTokMetric}
        cacheHitRate={cacheHitRate} truncated={truncated} requestsPerTurn={reqsPerTurn}
        error={latencyError} />

      {/* ── Voice: the speech models, under LLM requests (Mark, 2026-10-16) ─────
            Their own section: a speech call is billed to the budget like an LLM call
            (its seconds of audio at the Voice page's price per second), so Total Cost and
            the Spend cards count it while the LLM cards above leave it out. Always shown:
            every environment has speech (Edge TTS is built in), so an empty section means
            no calls in the window, not a feature that is off. */}
      <VoiceUsage data={voiceData} totalCost={totalCostUsd} />

      {/* ── Routers: what each costs, which models it chose ─────────────────────
            Below LLM requests (Mark, 2026-09-30), not at the top of the page: it is a
            detail view of one feature, below the overall spend, load and latency.
            Hidden while no router is defined and none served a call in the window, like
            the other sections for features nobody uses: a row of empty cards would read
            as broken rather than unused. */}
      {(routerData.configured > 0 || (routerData.routers || []).length > 0) && (
        <RouterInsights data={routerData} />
      )}

      {/* ── Outbound Internet: the sites the agents reached (Mark, 2026-10-03) ──
          Under Routers and above Tools, as asked. Which site, how often and how long;
          never what was sent. Fetched on its own path (fetchOutbound). */}
      <OutboundInternet
        summary={outbound}
        timing={outboundTiming}
        recent={outboundRecent}
        metric={outboundMetric}
        onMetric={setOutboundMetric}
        result={outboundResult}
        onResult={setOutboundResult}
        error={outboundError} />

      {/* ── Customised assistant instructions ────────────────────────────────
          Users who have edited SOUL.md or AGENTS.md through the producer API. Writing
          either one takes it OUT of platform management for that user — the only way the
          edit could survive the next platform update — so these are the agents a template
          change no longer reaches.

          `behind_template` is the reason this is on the dashboard rather than only in the
          per-user API. The failure mode of pinning is SILENCE: nothing errors, the user
          keeps working, and their agent simply never receives whatever went into the
          templates afterwards. An operator who ships a SOUL.md change has no other way to
          see how many agents it did not reach.

          Hidden entirely when nothing is pinned, which is the normal state — a row of
          zeros for an unused feature reads as a broken metric.

          NOT affected by the page's date range, unlike everything above it: a pin is
          current state, not an event in a window. A user who customised their file last
          month still has it customised today, and filtering to "last 7 days" would report
          zero and mean "nobody did this recently" while looking like "nobody did this". */}
      {(stats.prompt_files || []).length > 0 && (() => {
        const files = stats.prompt_files;
        const behind = files.reduce((s, f) => s + (f.behind_template || 0), 0);
        const pinned = files.reduce((s, f) => s + (f.pinned || 0), 0);
        // Fragment: the heading is a sibling of the grid inside this IIFE's return.
        return (
          <>
          <div className="section-head">
            <h2>Assistant instructions</h2>
          </div>
          <div className="dashboard-grid">
            <Card title="Customised assistant instructions"
                  subtitle={`${pinned.toLocaleString()} file${pinned === 1 ? '' : 's'} `
                            + 'no longer updated by the platform'}
                  span={6}>
              <HBarChart
                data={files.map(f => ({ name: f.name, count: f.pinned || 0 }))}
                nameWidth={120}
              />
            </Card>
            <Card title="Behind the current templates"
                  subtitle={behind === 0
                    ? 'every customised file was taken over on the current template version'
                    : 'customised before the template version now running'}
                  span={6}
                  empty={behind === 0 ? 'All up to date' : null}>
              <HBarChart
                data={files
                  .filter(f => (f.behind_template || 0) > 0)
                  .map(f => ({ name: f.name, count: f.behind_template }))}
                nameWidth={120}
                /* Amber, not red: being behind is a consequence the operator chose when
                   they allowed the edit, not a fault to be fixed. */
                colorFn={() => 'var(--warn-solid)'}
              />
            </Card>
          </div>
          </>
        );
      })()}

      {/* ── Tools, in two halves (above Language, then Operations) ──────────
          LEFT counts what the MODEL called, from `tool_call_logs.tool_name`.
          RIGHT counts what proxy-tools actually RAN, from `audit.tool_id`. The two are
          different questions, so they are two charts rather than one merged list.

          ⚠ THE TWO LISTS ARE DISJOINT BY CONSTRUCTION, and that is not cosmetic. Five
          tools (stt, tts, image_caption, image_fetch, proofread) have compiled runtime
          wrappers whose model-facing name equals the `tool_id` they invoke, so they used to
          appear on BOTH cards with different counts — different events, different retention
          windows, and nothing here to say which number was right. `get_tool_stats` now
          excludes every `tool_catalogue.tool_id` from the left chart.

          ⚠ `call_mcp_tool` / `get_mcp_tool` ARE EXCLUDED TOO, and do not put them back.
          They are not catalogue ids, so the exclusion above cannot reach them; they are
          dropped by name (`_not_a_gateway_entry_point`). They counted MODEL DECISIONS while
          the right card counts INVOCATIONS, and side by side they invited a subtraction with
          no available answer: measured on DEV 2026-09-23, 959 against 1409, of which ~18%
          were wrapper tools that never write a `call_mcp_tool` row and ~21% were retries
          below the model layer. Nothing joins the two tables — `audit`'s
          `parent_request_id` / `trace_id` / `session_id` are NULL on every row and
          `tool_call_logs` holds only (user_id, tool_name, model, timestamp) — so no query
          can reconcile them. Mark's call, 2026-09-23: drop the bars.

          So this chart is now the runtime's OWN registry and nothing else, and it carries no
          number that can be compared against the card beside it. Known cost: those bars were
          the only platform-tool signal outliving `audit`'s 5-week prune, so on a longer
          window the page shows no platform-tool volume at all. The fix for that is a
          correlation id, not these bars.

          WHICH PAGE FILTERS REACH THE RIGHT CHART (checked 2026-09-23, `au_clauses` in
          services/dashboard.py). Date range, user, tier and channel ALL apply: the date
          window is `audit.timestamp` with the same half-open shape as every other card,
          tier and channel arrive as subqueries on `users` / `message_logs` keyed off
          `audit.user_id`. `model` cannot — `audit` has no such column — so the service
          returns nothing and raises `model_filter_ignored` rather than showing numbers that
          quietly ignore the selection. `source` reaches NEITHER tool chart: it is
          `usage_logs.source` (llm / tools / correction, i.e. what originated an LLM call)
          and no tool table carries it, so both charts ignore it identically.

          ⚠ "shorter history" is why the right card can empty while the left still has data.
          `audit` has no `_daily` rollup and is pruned on `audit_weeks` (5 weeks by default),
          while `tool_call_logs` rolls up and keeps 13. On a range older than the audit window
          the right card goes blank, which looks exactly like "no platform tools ran". Since
          the gateway bars came off the left, nothing else on the page contradicts that
          impression, so read an empty right card against the window before believing it.

          ABOVE the two bar charts: tool call latency and turn latency, moved here from the
          section now called LLM requests (Mark, 2026-09-30). Same cards, same data path
          (fetchLatency), with their own error notice so a failure is named in this section.
          The cards are components/dashboard/ToolsSection, shared with the load tests'
          report, which computes the same definitions from a run's own events. */}
      <ToolsSection
        toolLatency={toolLatency} tool={toolLatTool} onTool={setToolLatTool}
        turnLatency={turnLatency}
        topTools={stats.top_tools} platformTools={stats.mcp_tools}
        platformToolsIgnored={Boolean(stats.mcp_tools_meta?.model_filter_ignored)}
        error={toolLatencyError} />

      {/* ── Language, then the outbound correction (under Tools, Mark 2026-10-03) ─
            What language the agents are actually replying in, from the detection step
            that runs on every outbound message before the correction decides whether to
            rewrite it. Counted across every message including the ones nothing was done
            to, so it answers a question about the FLEET, not about the feature.

            The correction's own two cards follow in the same grid (Mark, 2026-10-03:
            the two are one feature, detection then rewrite). Hidden rather than zeroed
            when the toggle is off: a "0 changed / 0 unchanged" panel on a disabled
            feature reads as a broken metric.

            No heading card: removed on request 2026-09-22. Both cards are self-titled,
            so a heading above them only restated them and cost a full-width row.

            ⚠ Still gated on the correction being enabled, and that is not a UI choice.
            The detection call only happens as part of the correction tool, so with the
            toggle off there is no language data being written at all and the section
            would be permanently empty. Showing an empty histogram would read as "the
            agents speak nothing".

            Both counts honour the page's date range. The service hands
            `get_correction_language_stats` the same tc_where/rollup_where pair the tool
            charts use, and `correction_logs` / `correction_logs_daily` carry the
            `timestamp` / `date` columns those clauses need — asserted in
            test_outbound_correction.py::TestTheDashboardContract. */}
      {stats.correction?.enabled && (() => {
        const langs = stats.correction.by_language || [];
        const total = langs.reduce((s, l) => s + l.count, 0);
        const it = langs.find(l => l.name === 'IT')?.count || 0;
        const unknown = langs.find(l => l.name === 'unknown')?.count || 0;
        // IT vs everything else is the operationally useful split: only Italian is ever
        // rewritten, so this is the share of outbound traffic the correction applies to.
        // `unknown` is kept OUT of "other languages" and shown as its own bar, because it
        // is a detection failure rather than a language and lumping it in would quietly
        // inflate the count of messages we believe we identified.
        const split = [
          { name: 'Italian', count: it },
          { name: 'Other languages', count: total - it - unknown },
          { name: 'Not identified', count: unknown },
        ].filter(r => r.count > 0);
        const c = stats.correction;
        const checked = (c.changed || 0) + (c.unchanged || 0);
        const pct = checked ? Math.round((c.changed / checked) * 100) : 0;
        // Fragment because this group is the return of an IIFE and has a sibling: the
        // section heading sits outside the grid, not inside it.
        return (
          <>
          <div className="section-head">
            <h2>Language</h2>
          </div>
          <div className="dashboard-grid">
            <Card title="Message languages"
                  subtitle="detected ISO 639-1 codes"
                  span={6}
                  empty={langs.length === 0 ? EMPTY_TEXT : null}>
              <HBarChart
                data={langs}
                nameWidth={80}
                /* Green for the language that gets rewritten, orange for a detection
                   failure so a growing one is visible, the default for the rest. */
                colorFn={(row) => row.name === 'IT'
                  ? 'var(--ok-solid)'
                  : row.name === 'unknown'
                    ? 'var(--series-3)'
                    : 'linear-gradient(90deg, var(--series-1), var(--series-4))'}
              />
            </Card>
            <Card title="Italian vs the rest"
                  subtitle="share the correction applies to"
                  span={6}
                  empty={split.length === 0 ? EMPTY_TEXT : null}>
              <HBarChart
                data={split}
                nameWidth={130}
                colorFn={(row) => row.name === 'Italian'
                  ? 'var(--ok-solid)'
                  : row.name === 'Not identified'
                    ? 'var(--series-3)'
                    : 'var(--ink-4)'}
              />
            </Card>
            <Card title="Outbound correction"
                  subtitle={`${checked.toLocaleString()} message${checked === 1 ? '' : 's'} checked · ${pct}% rewritten`}
                  span={6}
                  empty={checked === 0 ? 'No messages checked yet' : null}>
              <HBarChart
                data={[
                  { name: 'Rewritten', count: c.changed || 0 },
                  { name: 'Left as written', count: c.unchanged || 0 },
                ]}
                nameWidth={120}
                /* Green for a rewrite, grey for a pass-through. Grey rather than red
                   because "left as written" is a correct outcome, not a failure. */
                colorFn={(row) => row.name === 'Rewritten' ? 'var(--ok-solid)' : 'var(--ink-4)'}
              />
            </Card>
            <Card title="Why a message was left as written"
                  subtitle="reason for each unchanged message"
                  span={6}
                  empty={(c.unchanged_by_reason || []).length === 0
                    ? 'Every message was rewritten' : null}>
              <HBarChart data={c.unchanged_by_reason || []} nameWidth={150} />
            </Card>
          </div>
          </>
        );
      })()}

      </>)}
      {/* ══ End of Overview tab content ═══════════════════════════════════ */}

      {/* ══ Rankings tab ═════════════════════════════════════════════════════
            One leaderboard: every user with all four figures, ranked by the one the tab
            picks, with their share of the fleet and their move since the previous window.
            Under the page's window and cohort, like everything else.
            ════════════════════════════════════════════════════════════════ */}
      {activeTab === 'rankings' && (
        <Leaderboard
          board={board}
          sort={lbSort}
          onSort={setLbSort}
          error={boardError}
          onRetry={fetchBoard}
          narrowedMessages={Boolean(filters.model || filters.source)}
        />
      )}

      {/* ── Operations footer — shown only on the Overview tab; out of place on
            Rankings, which is a pure analytical view. ───────────────────────────── */}
      {activeTab === 'overview' && (<>
        <div className="section-head">
          <h2>Operations</h2>
          <p>environmental footprint</p>
        </div>
        <div className="dashboard-grid">
          <Card title="Environmental footprint"
                subtitle="estimated from the token volume in this window"
                help="Estimated from published per-model averages per million tokens: about 1.3 L of water and 1.8 kg of CO₂ for Opus, 0.5 L and 0.65 kg for Sonnet, 0.2 L and 0.4 kg for MiniMax and Qwen. Tokens are split across models by their share of cost, so read it as an order of magnitude."
                span={12}
                empty={env ? null : 'No usage in the selected window, environmental footprint not computable.'}>
            {env && (
              <dl className="footprint-grid">
                <div className="footprint-figure">
                  <dt><Icon name="globe" size={14} /> Water</dt>
                  <dd><span className="num">{env.waterLiters.toFixed(1)}</span> L</dd>
                </div>
                <div className="footprint-figure">
                  <dt>Bottles (500 mL)</dt>
                  <dd><span className="num">{env.bottles500ml.toFixed(1)}</span></dd>
                </div>
                <div className="footprint-figure">
                  <dt><Icon name="cloud" size={14} /> Carbon</dt>
                  <dd><span className="num">{env.co2Kg.toFixed(2)}</span> kg CO₂</dd>
                </div>
              </dl>
            )}
          </Card>
        </div>
      </>)}
      </div>
    </div>
  );
}

export default Dashboard;
